import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleNotch } from "@phosphor-icons/react";
import { apiGet } from "@/lib/api-error";
import { formatCurrency } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { MonthlyBarChart } from "@/components/therapist/earnings/MonthlyBarChart";
import { SectionShell } from "@/components/therapist/settings/section-primitives";
import { StatCard, SessionPayoutList } from "@/components/therapist/finances/FinanceRows";
import { PayoutsTab } from "@/components/therapist/finances/PayoutsTab";
import { InvoicesTab } from "@/components/therapist/finances/InvoicesTab";
import type { Preset } from "@/types";

export const Route = createFileRoute("/dashboard/therapist/finances")({
  component: TherapistFinancesPage,
});

type Tab = "earnings" | "payouts" | "invoices";

interface SessionRow {
  id: string;
  scheduledAt: string;
  clientName: string;
  serviceName: string | null;
  therapistEarnings: number | null;
  paymentStatus: string;
  payoutId: string | null;
  payoutPaidAt: string | null;
}

interface MonthlyPoint {
  month: string;
  earnings: number;
  sessions: number;
}

interface EarningsResponse {
  summary: {
    totalEarnings: number;
    pendingPayout: number;
    paidOut: number;
    sessionCount: number;
  };
  sessions: SessionRow[];
  monthly: MonthlyPoint[];
  range: { from: string | null; to: string | null };
}

interface SubscriptionInvoiceRow {
  id: string;
  receiptNumber: string | null;
  planType: "semiannual" | "annual" | "custom";
  amountNet: number;
  amountVat: number;
  amountGross: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  paymentMethod: "monri" | "bank_transfer" | "manual";
  status: "completed";
  completedAt: string | null;
  createdAt: string;
}

interface SubscriptionLedgerResponse {
  rows: SubscriptionInvoiceRow[];
  totalPaid: number;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function presetRange(preset: Preset): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === "thisMonth") {
    return { from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDate(today) };
  }
  if (preset === "lastMonth") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: isoDate(start), to: isoDate(end) };
  }
  if (preset === "last3Months") {
    const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    return { from: isoDate(start), to: isoDate(today) };
  }
  return { from: isoDate(new Date(today.getFullYear(), 0, 1)), to: isoDate(today) };
}

const PRESETS: Preset[] = ["thisMonth", "lastMonth", "last3Months", "thisYear", "custom"];
const TABS: Tab[] = ["earnings", "payouts", "invoices"];

function sessionCountWord(n: number): string {
  return n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "sesije" : "sesija";
}

function TherapistFinancesPage() {
  const { t } = useTranslation();
  const monthNames = t("calendar.months", { returnObjects: true }) as string[];

  const [tab, setTab] = useState<Tab>("earnings");

  const [preset, setPreset] = useState<Preset>("thisMonth");
  const defaultRange = useMemo(() => presetRange("thisMonth"), []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);

  const choosePreset = (p: Preset) => {
    setPreset(p);
    if (p !== "custom") {
      const r = presetRange(p);
      setFrom(r.from);
      setTo(r.to);
    }
  };

  const earningsQuery = useQuery<EarningsResponse>({
    queryKey: ["therapist", "earnings", from, to],
    queryFn: () =>
      apiGet<EarningsResponse>(
        `/api/therapist/earnings?from=${from}&to=${to}`,
        t("therapist.earnings.errorLoading"),
      ),
    enabled: tab === "earnings",
  });

  const payoutsQuery = useQuery<EarningsResponse>({
    queryKey: ["therapist", "earnings", "all-time"],
    queryFn: () =>
      apiGet<EarningsResponse>(
        "/api/therapist/earnings",
        t("therapist.finances.errorLoadingPayouts"),
      ),
    enabled: tab === "payouts" || tab === "invoices",
  });

  const invoicesQuery = useQuery<SubscriptionLedgerResponse>({
    queryKey: ["therapist", "subscription-ledger"],
    queryFn: () =>
      apiGet<SubscriptionLedgerResponse>(
        "/api/me/subscription-ledger",
        t("therapist.finances.errorLoadingInvoices"),
      ),
    enabled: tab === "invoices",
  });

  const isLoading =
    (tab === "earnings" && earningsQuery.isFetching) ||
    (tab === "payouts" && payoutsQuery.isFetching) ||
    (tab === "invoices" && (invoicesQuery.isFetching || payoutsQuery.isFetching));

  const earnings = earningsQuery.data?.summary ?? {
    totalEarnings: 0,
    pendingPayout: 0,
    paidOut: 0,
    sessionCount: 0,
  };
  const earningsSessions = earningsQuery.data?.sessions ?? [];
  const monthly = earningsQuery.data?.monthly ?? [];

  const payouts = payoutsQuery.data?.summary ?? {
    totalEarnings: 0,
    pendingPayout: 0,
    paidOut: 0,
    sessionCount: 0,
  };
  const payoutSessions = payoutsQuery.data?.sessions ?? [];

  const invoices = invoicesQuery.data?.rows ?? [];
  const totalPaidToPlatform = invoicesQuery.data?.totalPaid ?? 0;

  return (
    <div className="relative mx-auto max-w-6xl space-y-6 pb-14">
      <header className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1
              className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {t("therapist.finances.title")}
            </h1>
            {isLoading && <CircleNotch className="size-5 shrink-0 animate-spin text-muted-foreground" />}
          </div>
          <p className="mt-1.5 text-sm text-muted-foreground">{t("therapist.finances.subtitle")}</p>
        </div>

        <SegmentedFilter
          ariaLabel={t("therapist.finances.title")}
          options={TABS.map((value) => ({ value, label: t(`therapist.finances.tabs.${value}`) }))}
          value={tab}
          onChange={setTab}
        />
      </header>

      {/* Date filter, samo na Zarada tabu */}
      {tab === "earnings" && (
        <>
          <SegmentedFilter
            ariaLabel={t("therapist.earnings.period")}
            options={PRESETS.map((p) => ({ value: p, label: t(`therapist.earnings.${p}`) }))}
            value={preset}
            onChange={choosePreset}
          />
          {preset === "custom" && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-border/60 bg-card px-5 py-4 shadow-sm">
              <label className="flex items-center gap-2.5">
                <span className="text-sm font-medium text-foreground">{t("therapist.earnings.from")}</span>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-9 w-40 tabular-nums"
                />
              </label>
              <label className="flex items-center gap-2.5">
                <span className="text-sm font-medium text-foreground">{t("therapist.earnings.to")}</span>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-9 w-40 tabular-nums"
                />
              </label>
            </div>
          )}
        </>
      )}

      {/* Stat kartice, panel i tabela za Zarada tab */}
      {tab === "earnings" && (
        <>
          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard label={t("therapist.earnings.totalEarnings")} value={formatCurrency(earnings.totalEarnings)} tone="primary" />
            <StatCard label={t("therapist.earnings.pendingPayout")} value={formatCurrency(earnings.pendingPayout)} tone="pending" />
            <StatCard label={t("therapist.earnings.paidOut")} value={formatCurrency(earnings.paidOut)} tone="paid" />
          </section>

          <SectionShell
            title={t("therapist.earnings.monthlyChart")}
            description={`${monthly.length} ${monthly.length === 1 ? "mjesec" : "mjeseci"} u odabranom periodu.`}
          >
            <MonthlyBarChart data={monthly} monthNames={monthNames} emptyLabel={t("therapist.earnings.noData")} />
          </SectionShell>

          <SectionShell
            title={t("therapist.earnings.sessionsTable")}
            description={`${earnings.sessionCount} ${sessionCountWord(earnings.sessionCount)} u odabranom periodu.`}
          >
            <SessionPayoutList
              sessions={earningsSessions}
              isFetching={earningsQuery.isFetching}
              emptyLabel={t("therapist.earnings.noData")}
              statusLabels={{
                paid: t("therapist.earnings.statusPaid"),
                pending: t("therapist.earnings.statusPending"),
                unpaid: t("therapist.earnings.statusUnpaid"),
              }}
            />
          </SectionShell>
        </>
      )}

      {tab === "payouts" && (
        <PayoutsTab summary={payouts} sessions={payoutSessions} isFetching={payoutsQuery.isFetching} />
      )}

      {tab === "invoices" && (
        <InvoicesTab
          invoices={invoices}
          isFetching={invoicesQuery.isFetching}
          totalPaidToPlatform={totalPaidToPlatform}
          platformPaid={payouts.paidOut}
          platformOwes={payouts.pendingPayout}
        />
      )}
    </div>
  );
}
